import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { PawPrint, FileSignature, CheckCircle, AlertCircle, Loader2, DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const PublicForm = () => {
  const { templateId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`/forms/${templateId}`);
        setForm(response.data);
        
        // Initialize form data with empty values
        const initialData = {};
        response.data.fields?.forEach(field => {
          initialData[field.id] = field.type === 'checkbox' ? false : '';
        });
        setFormData(initialData);
      } catch (err) {
        setError(err.response?.data?.detail || 'Form not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchForm();
  }, [templateId]);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = form.fields?.filter(field => 
      field.required && !formData[field.id]
    );
    
    if (missingFields?.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    if (form.terms_and_conditions && !agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post('/shelter/form-submissions', {
        template_id: templateId,
        organization_id: form.organization_id,
        form_type: form.form_type,
        form_data: formData,
        agreed_to_terms: agreedToTerms
      });
      
      // Check if payment is required
      if (form.payment_enabled && form.payment_amount > 0) {
        // If there's a Stripe payment link, redirect
        if (form.stripe_payment_link) {
          toast.success('Form submitted! Redirecting to payment...');
          // Append submission ID to track payment
          const paymentUrl = new URL(form.stripe_payment_link);
          paymentUrl.searchParams.set('client_reference_id', response.data?.submission_id || templateId);
          setTimeout(() => {
            window.location.href = paymentUrl.toString();
          }, 1500);
          return;
        } else {
          // Show payment required message without redirect
          setSubmitted(true);
          toast.success('Form submitted! Please complete payment to finalize your application.');
          return;
        }
      }
      
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.id,
      required: field.required,
      placeholder: field.placeholder || ''
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return (
          <Input
            {...commonProps}
            type={field.type === 'phone' ? 'tel' : field.type}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            {...commonProps}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            rows={4}
          />
        );
      
      case 'date':
        return (
          <Input
            {...commonProps}
            type="date"
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
          />
        );
      
      case 'select':
        return (
          <Select value={formData[field.id] || ''} onValueChange={(v) => handleFieldChange(field.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt, idx) => (
                <SelectItem key={idx} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'radio':
        return (
          <RadioGroup value={formData[field.id] || ''} onValueChange={(v) => handleFieldChange(field.id, v)}>
            {field.options?.map((opt, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`${field.id}_${idx}`} />
                <Label htmlFor={`${field.id}_${idx}`}>{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={formData[field.id] || false}
              onCheckedChange={(c) => handleFieldChange(field.id, c)}
            />
            <Label htmlFor={field.id}>{field.placeholder || 'Yes'}</Label>
          </div>
        );
      
      case 'file':
        return (
          <Input
            {...commonProps}
            type="file"
            onChange={(e) => handleFieldChange(field.id, e.target.files?.[0]?.name || '')}
          />
        );
      
      case 'address':
        return (
          <Textarea
            {...commonProps}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder="Enter full address"
            rows={3}
          />
        );
      
      case 'payment':
        return (
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="font-medium text-green-800">Payment Amount: ${form.payment_amount}</p>
            <p className="text-sm text-green-600">{form.payment_description}</p>
            {form.stripe_payment_link && (
              <a 
                href={form.stripe_payment_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Pay Now
              </a>
            )}
          </div>
        );
      
      default:
        return (
          <Input
            {...commonProps}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
          <p className="mt-4 text-stone-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-stone-800 mb-2">Form Not Found</h2>
            <p className="text-stone-600">{error}</p>
            <Button className="mt-4" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Thank You!</h2>
            <p className="text-stone-600 mb-4">Your form has been submitted successfully.</p>
            
            {/* Payment Reminder */}
            {form.payment_enabled && form.payment_amount > 0 && !form.stripe_payment_link && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <DollarSign className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                <p className="font-medium text-amber-800">Payment Required: ${form.payment_amount.toFixed(2)}</p>
                <p className="text-sm text-amber-700 mt-1">
                  Please contact the organization to complete your payment.
                </p>
              </div>
            )}
            
            {form.organization && (
              <p className="text-sm text-stone-500 mt-4">
                {form.organization.organization_name || form.organization.name} will review your submission and contact you soon.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <PawPrint className="w-12 h-12 mx-auto text-primary mb-4" />
          {form.organization && (
            <p className="text-sm text-stone-500 mb-2">
              {form.organization.organization_name || form.organization.name}
            </p>
          )}
          <h1 className="text-2xl font-bold text-stone-800">{form.title}</h1>
          {form.description && (
            <p className="text-stone-600 mt-2">{form.description}</p>
          )}
        </div>

        {/* Header Text */}
        {form.header_text && (
          <Card className="mb-6 bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <p className="text-stone-700 whitespace-pre-wrap">{form.header_text}</p>
            </CardContent>
          </Card>
        )}

        {/* Payment Notice */}
        {form.payment_enabled && form.payment_amount > 0 && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="pt-4 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Payment Required: ${form.payment_amount.toFixed(2)}
                </h3>
                {form.payment_description && (
                  <p className="text-sm text-green-700 mt-1">{form.payment_description}</p>
                )}
                <p className="text-xs text-green-600 mt-2">
                  {form.stripe_payment_link 
                    ? 'You will be redirected to complete payment after submitting this form.'
                    : 'Payment details will be provided after submission.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {form.fields?.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={field.id} className="flex items-center gap-1">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  {renderField(field)}
                </div>
              ))}

              {/* Terms and Conditions */}
              {form.terms_and_conditions && (
                <div className="border-t pt-6">
                  <div className="bg-stone-50 p-4 rounded-lg mb-4 max-h-40 overflow-y-auto">
                    <p className="text-sm text-stone-600 whitespace-pre-wrap">{form.terms_and_conditions}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                    />
                    <Label htmlFor="terms" className="text-sm">
                      I have read and agree to the terms and conditions
                    </Label>
                  </div>
                </div>
              )}

              {/* Digital Signature Notice */}
              {form.require_signature && (
                <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-50 p-3 rounded-lg">
                  <FileSignature className="w-5 h-5" />
                  <span>By submitting this form, you are providing your digital signature.</span>
                </div>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Form'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer Text */}
        {form.footer_text && (
          <p className="text-center text-sm text-stone-500 mt-6">{form.footer_text}</p>
        )}
      </div>
    </div>
  );
};

export default PublicForm;
